(() => {
  if (window.VistoVendor) return

  const configElement = document.querySelector('[data-visto-vendor-config]')
  const resourceState = new Map()

  const readConfig = () => {
    if (!configElement?.textContent) return {}

    try {
      return JSON.parse(configElement.textContent)
    } catch (error) {
      console.warn('[VistoVendor] Invalid vendor config.', error)
      return {}
    }
  }

  const config = readConfig()

  const getResourceState = (key) => {
    if (!resourceState.has(key)) {
      resourceState.set(key, {
        status: 'idle',
        promise: null,
        error: null,
      })
    }

    return resourceState.get(key)
  }

  const updateResourceState = (key, status, promise = null, error = null) => {
    const state = getResourceState(key)
    state.status = status
    state.promise = promise
    state.error = error
    window.dispatchEvent(new CustomEvent('visto:vendor:state', {
      detail: { key, status, error },
    }))
    return state
  }

  const loadScript = (src, globalName) => {
    if (globalName && window[globalName]) return Promise.resolve(window[globalName])

    const existing = document.querySelector(`script[data-visto-vendor-src="${src}"]`)
    if (existing?.dataset.vistoVendorLoaded === 'true') {
      return Promise.resolve(globalName ? window[globalName] : existing)
    }

    return new Promise((resolve, reject) => {
      const script = existing || document.createElement('script')

      script.dataset.vistoVendorSrc = src
      script.async = true
      script.crossOrigin = 'anonymous'
      script.src = src

      script.addEventListener('load', () => {
        script.dataset.vistoVendorLoaded = 'true'
        resolve(globalName ? window[globalName] : script)
      }, { once: true })

      script.addEventListener('error', () => {
        reject(new Error(`Failed to load Visto vendor script: ${src}`))
      }, { once: true })

      if (!existing) document.head.append(script)
    })
  }

  const loadStyle = (href) => {
    const existing = document.querySelector(`link[data-visto-vendor-href="${href}"]`)
    if (existing) return Promise.resolve(existing)

    return new Promise((resolve, reject) => {
      const link = document.createElement('link')
      link.dataset.vistoVendorHref = href
      link.rel = 'stylesheet'
      link.href = href
      link.crossOrigin = 'anonymous'
      link.addEventListener('load', () => resolve(link), { once: true })
      link.addEventListener('error', () => reject(new Error(`Failed to load Visto vendor style: ${href}`)), { once: true })
      document.head.append(link)
    })
  }

  const loadScriptResource = (key, resource) => {
    const state = getResourceState(key)
    if (state.promise) return state.promise

    const promise = resource.scripts.reduce(
      (chain, src) => chain.then(() => loadScript(src, resource.global)),
      Promise.resolve()
    ).then(() => {
      updateResourceState(key, 'loaded')
      return resource.global ? window[resource.global] : true
    }).catch((error) => {
      updateResourceState(key, 'error', null, error)
      throw error
    })

    updateResourceState(key, 'loading', promise)
    return promise
  }

  const loadModuleResource = (key, resource) => {
    const state = getResourceState(key)
    if (state.promise) return state.promise

    const promise = import(resource.module).then((module) => {
      updateResourceState(key, 'loaded')
      return module
    }).catch((error) => {
      updateResourceState(key, 'error', null, error)
      throw error
    })

    updateResourceState(key, 'loading', promise)
    return promise
  }

  const loadReact = () => {
    if (!config.react || !config.reactDom) {
      return Promise.reject(new Error('Visto React vendor config is missing.'))
    }

    return loadScriptResource('react', config.react)
      .then(() => loadScriptResource('reactDom', config.reactDom))
      .then(() => ({
        React: window.React,
        ReactDOM: window.ReactDOM,
      }))
  }

  const loadFramerMotion = () => {
    if (!config.framerMotion) return Promise.reject(new Error('Visto Framer Motion config is missing.'))
    return loadModuleResource('framerMotion', config.framerMotion)
  }

  const loadFramerMotionDom = () => {
    if (!config.framerMotionDom) return loadFramerMotion()
    return loadModuleResource('framerMotionDom', config.framerMotionDom)
  }

  const loadSwiper = () => {
    if (!config.swiper) return Promise.reject(new Error('Visto Swiper config is missing.'))

    const stylePromises = (config.swiper.styles || []).map(loadStyle)

    return Promise.all(stylePromises)
      .then(() => loadScriptResource('swiper', config.swiper))
      .then(() => window.Swiper)
  }

  const status = () => Array.from(resourceState.entries()).reduce((summary, [key, state]) => {
    summary[key] = {
      status: state.status,
      error: state.error ? state.error.message : null,
    }
    return summary
  }, {})

  window.VistoVendor = {
    config,
    loadReact,
    loadFramerMotion,
    loadFramerMotionDom,
    loadSwiper,
    loadScript,
    loadStyle,
    status,
  }
})()
